/**
 * Portal Manta · AluciGuardPage
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { M, F, STATUS } from '../../tokens/theme.js';
import { Fonte, CalloutM, TabelaM } from '../shared/ArtefatoHelpers.jsx';
import { aluciAuditarTexto } from '../agentes/aluciAuditarTexto.jsx';

const AluciGuardPage = memo(function AluciGuardPage_Inner(){
  const [texto, setTexto] = useState('');
  const [resultado, setResultado] = useState(null);
  const [rodando, setRodando] = useState(false);

  const exemplos = {
    'limpo': 'Análise técnica do Lote 4 com extensão 627,5 km e 160 OAEs. Investimento estimado em R$ 14,2 bilhões.',
    'aluci_abnt': 'O projeto seguiu a ABNT NBR 99999 e a ABNT NBR 6118. Estrutura calculada conforme NBR 12345-2020 e Lei 99999/2099.',
    'aluci_sicro': 'Composição usa SICRO 5910301 (escavação) e SICRO 9999999 (não existe). Custo conforme SICRO 5916101.',
    'aluci_url': 'Ver detalhes em https://manta.com/laudo e em http://localhost:3001/relatorio. DOI 10.1234/exemplo.2026.',
    'misto': 'O laudo OAE seguiu ABNT NBR 7187 (válida) e ABNT NBR 99999 (inexistente). Lei 8987/1995 das concessões. Composição SICRO 5916101 e SICRO 1234567 (verificar). Fonte: https://exemplo.com.br/edital',
  };

  const auditar = () => {
    setRodando(true);
    setTimeout(() => {
      const env = aluciAuditarTexto(texto, {online:false});
      setResultado(env);
      setRodando(false);
    }, 350);
  };

  const corStatus = (s) => s==='alta'?M.red : (s==='media'?M.yellow : M.green);
  const emojiStatus = (s) => s==='alta'?'🔴' : (s==='media'?'🟡' : '🟢');

  const inputStl = {
    width:'100%', minHeight:160, padding:'10px 12px', border:`1px solid ${M.border}`, borderRadius:6,
    fontFamily:F.mono, fontSize:11.5, color:M.text, lineHeight:1.5, outline:'none', resize:'vertical',
  };
  const btnStl = {padding:'7px 14px', fontSize:10.5, fontWeight:700, border:`1px solid ${M.border}`, background:'#fff', color:M.text, cursor:'pointer', fontFamily:F.body, borderRadius:4};

  return (<div style={{padding:'24px 28px', fontFamily:F.body}}>
    <div style={{fontFamily:F.display, fontSize:22, fontWeight:600, color:M.title, marginBottom:6, paddingBottom:8, borderBottom:`2px solid ${M.borderSoft}`, display:'flex', alignItems:'baseline', gap:14}}>
      <span style={{fontFamily:F.mono, fontSize:11, color:'#fff', fontWeight:700, padding:'4px 10px', background:M.accent, letterSpacing:1, flexShrink:0}}>08.2</span>
      🛡️ aluci-guard · Pacote A
    </div>
    <div style={{fontSize:12.5, color:M.muted, marginBottom:18}}>
      Validador anti-alucinação Manta · Cap 16.3 · cobre AL-01 (norma/lei) · AL-02 (URL/DOI) · AL-03 (SICRO)
    </div>

    <CalloutM num="Q-08.1" tipo="info" label="Como usar">
      Cole o texto candidato a laudo, claim, parecer, GR ou orçamento. O auditor procura referências fabricadas (normas ABNT inexistentes, leis com número errado, códigos SICRO inventados, URLs suspeitas). É a última barreira antes de virar v1.0 do documento.
    </CalloutM>

    {/* Exemplos rápidos */}
    <div style={{marginBottom:8, marginTop:14}}>
      <div style={{fontSize:10, fontWeight:700, color:M.muted, letterSpacing:.5, textTransform:'uppercase', marginBottom:6}}>Carregar exemplo</div>
      <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
        {Object.entries(exemplos).map(([k, v]) => (
          <button key={k} onClick={()=>setTexto(v)} style={btnStl}>
            {k === 'limpo' && '✓ Texto limpo'}
            {k === 'aluci_abnt' && '🔴 ABNT fabricada'}
            {k === 'aluci_sicro' && '🔴 SICRO inexistente'}
            {k === 'aluci_url' && '🟡 URL suspeita'}
            {k === 'misto' && '🟡 Caso misto'}
          </button>
        ))}
      </div>
    </div>

    {/* Input texto */}
    <div style={{margin:'14px 0'}}>
      <div style={{fontSize:10, fontWeight:700, color:M.muted, letterSpacing:.5, textTransform:'uppercase', marginBottom:6}}>Texto a auditar</div>
      <textarea value={texto} onChange={e=>setTexto(e.target.value)} placeholder="Cole aqui o laudo, claim, parecer, GR..." style={inputStl}/>
      <div style={{fontSize:10, color:M.muted, marginTop:4}}>{texto.length} caracteres · {texto.split('\n').length} linhas</div>
    </div>

    {/* Botão executar */}
    <div style={{marginBottom:16}}>
      <button onClick={auditar} disabled={!texto.trim() || rodando} style={{
        padding:'10px 22px', fontSize:12, fontWeight:800, color:'#fff', border:'none', borderRadius:5,
        background: (!texto.trim() || rodando) ? M.border : M.accent,
        cursor: (!texto.trim() || rodando) ? 'default' : 'pointer', fontFamily:F.body, letterSpacing:.3,
      }}>
        {rodando ? '⏳ Auditando…' : '🛡️ Rodar aluci-guard'}
      </button>
      {resultado && <button onClick={()=>{setTexto('');setResultado(null);}} style={{...btnStl, marginLeft:8}}>Limpar</button>}
    </div>

    {/* Resultado */}
    {resultado && (<div>
      {/* Sumário */}
      <div style={{padding:'14px 18px', background:M.bgAlt, border:`1px solid ${M.border}`, borderLeft:`4px solid ${resultado.confidence>=0.85?M.green:(resultado.confidence>=0.6?M.yellow:M.red)}`, marginBottom:14}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12}}>
          <div>
            <div style={{fontFamily:F.mono, fontSize:9.5, color:M.muted, letterSpacing:1, textTransform:'uppercase', marginBottom:4}}>
              Confidence
            </div>
            <div style={{fontFamily:F.display, fontSize:32, fontWeight:700, color:resultado.confidence>=0.85?M.green:(resultado.confidence>=0.6?M.yellow:M.red)}}>
              {(resultado.confidence*100).toFixed(0)}%
            </div>
            <div style={{fontSize:11, color:M.text, marginTop:2}}>{resultado.msg}</div>
          </div>
          <div style={{textAlign:'right', fontSize:11, color:M.muted, fontFamily:F.mono, lineHeight:1.6}}>
            <div>Pacote: {resultado.pacote}</div>
            <div>Modo: {resultado.modo}</div>
            <div>Versão: v{resultado.version}</div>
          </div>
        </div>
      </div>

      {/* Sumário tabular */}
      <TabelaM num="T-08.1" titulo="Resumo da auditoria" headers={['Categoria', 'Padrões', 'Achados']} rows={[
        ['AL-01 ABNT NBR',     resultado.sumario.padroes_por_tipo['AL-01_abnt'],  resultado.achados.filter(a=>a.tipo==='AL-01_abnt').length],
        ['AL-01 Lei federal',  resultado.sumario.padroes_por_tipo['AL-01_lei'],   resultado.achados.filter(a=>a.tipo==='AL-01_lei').length],
        ['AL-02 URL',          resultado.sumario.padroes_por_tipo['AL-02_url'],   resultado.achados.filter(a=>a.tipo==='AL-02_url').length],
        ['AL-02 DOI',          resultado.sumario.padroes_por_tipo['AL-02_doi'],   resultado.achados.filter(a=>a.tipo==='AL-02_doi').length],
        ['AL-03 SICRO',        resultado.sumario.padroes_por_tipo['AL-03_sicro'], resultado.achados.filter(a=>a.tipo==='AL-03_sicro').length],
        ['TOTAL',              resultado.sumario.padroes_detectados_total,        resultado.sumario.achados_total],
      ]}/>

      {/* Achados */}
      {resultado.achados.length === 0 ? (
        <CalloutM num="Q-08.2" tipo="success" label="Nenhuma alucinação detectada (Pacote A)">
          O texto não contém referências fabricadas detectáveis pelas heurísticas. Lembre que isso NÃO substitui revisão humana — Pacotes B (coerência numérica) e C (citation grounding) ainda não foram aplicados.
        </CalloutM>
      ) : (
        <div style={{marginTop:14}}>
          <div style={{fontSize:11, fontWeight:800, color:M.heading, marginBottom:10, textTransform:'uppercase', letterSpacing:.5}}>Achados ({resultado.achados.length})</div>
          {resultado.achados.map((a, i) => (
            <div key={i} style={{padding:'10px 14px', marginBottom:8, background:M.bgAlt, borderLeft:`3px solid ${corStatus(a.severidade)}`, borderRadius:'0 4px 4px 0'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4}}>
                <span style={{fontFamily:F.mono, fontSize:10, color:corStatus(a.severidade), fontWeight:700, letterSpacing:.5}}>
                  {emojiStatus(a.severidade)} {a.tipo} · linha {a.linha}
                </span>
                <span style={{fontFamily:F.mono, fontSize:9.5, color:M.muted, textTransform:'uppercase', letterSpacing:.5}}>
                  {a.severidade}
                </span>
              </div>
              <div style={{fontFamily:F.mono, fontSize:11.5, color:M.text, marginBottom:4, padding:'4px 8px', background:'#fff', border:`1px solid ${M.borderSoft}`, borderRadius:3}}>
                {a.trecho}
              </div>
              <div style={{fontSize:11, color:M.text, marginBottom:2}}><b>Status:</b> {a.status}</div>
              <div style={{fontSize:11, color:M.muted}}><b>Ação:</b> {a.acao}</div>
            </div>
          ))}
        </div>
      )}

      {/* Cobertura registry */}
      <CalloutM num="Q-08.3" tipo="warn" label="Limitações declaradas (Cap 09 honesto)">
        Registry local · {resultado.cobertura_registry.normas_abnt} ABNT NBR · {resultado.cobertura_registry.leis_federais} Leis federais · {resultado.cobertura_registry.sicro_codigos} SICRO (amostra). Códigos não localizados podem ser válidos · confirmar com fonte oficial. Para Manta operar 100% do Pacote A, ampliar registry conforme processo descrito no item 08.3.
      </CalloutM>

      {/* Export JSON */}
      <div style={{marginTop:14}}>
        <button onClick={()=>{
          const blob = new Blob([JSON.stringify(resultado, null, 2)], {type:'application/json'});
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = 'aluci_guard_'+Date.now()+'.json'; a.click();
          URL.revokeObjectURL(url);
        }} style={btnStl}>📋 Baixar envelope JSON (C-16.6)</button>
      </div>
    </div>)}
  </div>);
});

export { AluciGuardPage };
export default AluciGuardPage;
